import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, blobErrMsg, errMsg } from "@/lib/api";

// Plays one answer at a time. In a live session the mic is paused while Observer speaks,
// so its own voice never ends up in the transcript, and resumed afterwards.
export function useReadAloud({ sessionId, meta, setMeta }) {
  const [speaking, setSpeaking] = useState({ n: null, phase: "idle" });
  const audio = useRef(null);
  const pausedByUs = useRef(false);
  const token = useRef(0);
  const live = useRef(false);
  live.current = !!meta && meta.mode === "live" && meta.status === "active";
  const paused = useRef(false);
  paused.current = !!meta?.paused;

  const setPaused = useCallback(async (value) => {
    const { data } = await api.post(`/sessions/${sessionId}/pause`, { paused: value, reason: "read_aloud" });
    setMeta(data);
  }, [sessionId, setMeta]);

  const finish = useCallback(async () => {
    token.current += 1;
    const a = audio.current;
    audio.current = null;
    if (a) {
      a.onended = null;
      a.onerror = null;
      a.pause();
      URL.revokeObjectURL(a.src);
    }
    setSpeaking({ n: null, phase: "idle" });
    if (pausedByUs.current) {
      pausedByUs.current = false;
      await setPaused(false).catch((e) => toast.error(`Could not resume listening: ${errMsg(e)}`));
    }
  }, [setPaused]);

  const speak = useCallback(async (n) => {
    const same = speaking.n === n;
    await finish();
    if (same) return;
    const mine = token.current;
    setSpeaking({ n, phase: "loading" });
    let blob;
    try {
      ({ data: blob } = await api.post(`/sessions/${sessionId}/answers/${n}/speak`, {}, { responseType: "blob", timeout: 60000 }));
    } catch (e) {
      if (mine === token.current) setSpeaking({ n: null, phase: "idle" });
      toast.error(await blobErrMsg(e));
      return;
    }
    if (mine !== token.current) return;
    if (live.current && !paused.current) {
      try {
        await setPaused(true);
        pausedByUs.current = true;
      } catch (e) {
        setSpeaking({ n: null, phase: "idle" });
        toast.error(`Not reading aloud: could not pause the mic (${errMsg(e)})`);
        return;
      }
    }
    if (mine !== token.current) return;
    const a = new Audio(URL.createObjectURL(blob));
    audio.current = a;
    a.onended = finish;
    a.onerror = () => {
      toast.error("This browser could not play the audio");
      finish();
    };
    setSpeaking({ n, phase: "playing" });
    a.play().catch((e) => {
      toast.error(`Could not play audio: ${e.message}`);
      finish();
    });
  }, [speaking.n, finish, sessionId, setPaused]);

  // A manual Pause/Resume during playback belongs to the user; don't undo it afterwards.
  const release = useCallback(() => {
    pausedByUs.current = false;
  }, []);

  useEffect(() => () => {
    const a = audio.current;
    if (a) {
      a.pause();
      URL.revokeObjectURL(a.src);
    }
    if (pausedByUs.current && live.current) api.post(`/sessions/${sessionId}/pause`, { paused: false, reason: "read_aloud" }).catch(() => {});
  }, [sessionId]);

  return { speaking, speak, stop: finish, release };
}
